import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "./context/AuthContext";
import { BranchProvider } from "./context/BranchContext";
import { SettingsProvider } from "./context/SettingsContext";
import { ThemeProvider, ThemeApplier } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute.jsx";
import AppShell from "./components/AppShell.jsx";
import Login from "./pages/Login.jsx";
import { Loader } from "./components/Loader.jsx";
import DeviceBlock from "./components/DeviceBlock.jsx";
import { isMobileOrTablet } from "./lib/device";
import { Toaster } from "@/components/ui/sonner";

// Code-split every page so the initial bundle stays tiny and each screen
// loads on demand — keeps the app fast and light to access.
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const Transaksi = lazy(() => import("./pages/Transaksi.jsx"));
const Rates = lazy(() => import("./pages/Rates.jsx"));
const Nasabah = lazy(() => import("./pages/Nasabah.jsx"));
const Kepatuhan = lazy(() => import("./pages/Kepatuhan.jsx"));
const Laporan = lazy(() => import("./pages/Laporan.jsx"));
const PapanHarga = lazy(() => import("./pages/PapanHarga.jsx"));
const Struk = lazy(() => import("./pages/Struk.jsx"));
const Settings = lazy(() => import("./pages/Settings.jsx"));
const RiwayatKurs = lazy(() => import("./pages/RiwayatKurs.jsx"));
const NotaPublic = lazy(() => import("./pages/NotaPublic.jsx"));
const Cabang = lazy(() => import("./pages/Cabang.jsx"));
const Users = lazy(() => import("./pages/Users.jsx"));
const Konsolidasi = lazy(() => import("./pages/Konsolidasi.jsx"));
const Audit = lazy(() => import("./pages/Audit.jsx"));
const BranchMoves = lazy(() => import("./pages/BranchMoves.jsx"));
const LaporanKeuangan = lazy(() => import("./pages/LaporanKeuangan.jsx"));
const COA = lazy(() => import("./pages/COA.jsx"));
const PengaturanStruk = lazy(() => import("./pages/PengaturanStruk.jsx"));
const RingkasanBulanan = lazy(() => import("./pages/RingkasanBulanan.jsx"));
const ShiftClosing = lazy(() => import("./pages/ShiftClosing.jsx"));
const SaldoBank = lazy(() => import("./pages/SaldoBank.jsx"));
const SaldoRupiah = lazy(() => import("./pages/SaldoRupiah.jsx"));
const StokValas = lazy(() => import("./pages/StokValas.jsx"));
const SaldoUangLebih = lazy(() => import("./pages/SaldoUangLebih.jsx"));
const Piutang = lazy(() => import("./pages/Piutang.jsx"));
const Backup = lazy(() => import("./pages/Backup.jsx"));
const ResetData = lazy(() => import("./pages/ResetData.jsx"));
const HakAkses = lazy(() => import("./pages/HakAkses.jsx"));
const LogHapus = lazy(() => import("./pages/LogHapus.jsx"));
const MenuOrder = lazy(() => import("./pages/MenuOrder.jsx"));
const ScanKTP = lazy(() => import("./pages/ScanKTP.jsx"));

// The operator app is now mobile-friendly. Only a few heavy, table-dense pages
// (financial reports, chart of accounts, data backup) stay desktop-only — they
// render a friendly notice inside the shell so navigation stays available.
function DesktopOnly({ children }) {
  if (isMobileOrTablet()) return <DeviceBlock pageOnly />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
      <BranchProvider>
      <SettingsProvider>
      <BrowserRouter>
        <ThemeApplier />
        <Toaster position="top-right" richColors />
        <Suspense fallback={<Loader full />}>
        <Routes>
          <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/papan-harga" element={<PapanHarga />} />
          <Route path="/scan" element={<ScanKTP />} />
          <Route path="/nota/:id" element={<NotaPublic />} />
          <Route path="/admin" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="transaksi" element={<Transaksi />} />
            <Route path="shift" element={<ShiftClosing />} />
            <Route path="rates" element={<ProtectedRoute adminOnly><Rates /></ProtectedRoute>} />
            <Route path="riwayat-kurs" element={<RiwayatKurs />} />
            <Route path="nasabah" element={<Nasabah />} />
            <Route path="kepatuhan" element={<ProtectedRoute adminOnly><Kepatuhan /></ProtectedRoute>} />
            <Route path="laporan" element={<Laporan />} />
            <Route path="ringkasan-bulanan" element={<ProtectedRoute superAdminOnly><RingkasanBulanan /></ProtectedRoute>} />
            <Route path="saldo-rupiah" element={<SaldoRupiah />} />
            <Route path="kas" element={<Navigate to="/admin/saldo-rupiah" replace />} />
            <Route path="stok-valas" element={<StokValas />} />
            <Route path="saldo-uang-lebih" element={<SaldoUangLebih />} />
            <Route path="piutang" element={<Piutang />} />
            <Route path="rekonsiliasi" element={<Navigate to="/admin/shift" replace />} />
            <Route path="cabang" element={<ProtectedRoute adminOnly><Cabang /></ProtectedRoute>} />
            <Route path="konsolidasi" element={<ProtectedRoute superAdminOnly><Konsolidasi /></ProtectedRoute>} />
            <Route path="users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
            <Route path="hak-akses" element={<ProtectedRoute superAdminOnly><HakAkses /></ProtectedRoute>} />
            <Route path="audit" element={<ProtectedRoute adminOnly><Audit /></ProtectedRoute>} />
            <Route path="branch-moves" element={<BranchMoves />} />
            <Route path="laporan-keuangan" element={<ProtectedRoute adminOnly><DesktopOnly><LaporanKeuangan /></DesktopOnly></ProtectedRoute>} />
            <Route path="coa" element={<ProtectedRoute superAdminOnly><DesktopOnly><COA /></DesktopOnly></ProtectedRoute>} />
            <Route path="backup" element={<ProtectedRoute superAdminOnly><DesktopOnly><Backup /></DesktopOnly></ProtectedRoute>} />
            <Route path="reset-data" element={<ProtectedRoute superAdminOnly><ResetData /></ProtectedRoute>} />
            <Route path="log-hapus" element={<ProtectedRoute superAdminOnly><LogHapus /></ProtectedRoute>} />
            <Route path="urutan-menu" element={<ProtectedRoute superAdminOnly><MenuOrder /></ProtectedRoute>} />
            <Route path="rekening-bank" element={<ProtectedRoute adminOnly><SaldoBank /></ProtectedRoute>} />
            <Route path="mutasi-bank" element={<Navigate to="/admin/rekening-bank" replace />} />
            <Route path="saldo-bank" element={<Navigate to="/admin/rekening-bank" replace />} />
            <Route path="mutasi-kas" element={<Navigate to="/admin/saldo-rupiah" replace />} />
            <Route path="pengaturan-struk" element={<ProtectedRoute adminOnly><PengaturanStruk /></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
            <Route path="struk/:id" element={<Struk />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </SettingsProvider>
      </BranchProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
